import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useApp } from '../context/AppContext';
import NovaAvatarImg from './NovaAvatarImg';

const CHAT_CARD_SHADOW =
  '0 4px 32px rgba(153,53,86,0.10), 0 1px 4px rgba(0,0,0,0.06)';

const BORDER_MUTED = '#e8e0d8';

export default function NovaSection() {
  const navigate = useNavigate();
  const { setNovaDraft } = useApp();
  const m = useMediaQuery('(max-width: 780px)');
  const m600 = useMediaQuery('(max-width: 600px)');

  const pad = m600 ? '48px 16px' : m ? '48px 18px' : '64px 48px';

  const openNova = (draft) => {
    setNovaDraft(draft);
    navigate('/nova');
  };

  return (
    <section
      id="nova-ai"
      style={{
        padding: pad,
        backgroundColor: 'rgb(249, 246, 241)',
        scrollMarginTop: 88,
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: 'var(--muted)',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: 12,
          }}
        >
          NOVA — YOUR CONVERSATIONAL LAYER
        </p>

        <h2
          className="serif"
          style={{
            fontSize: m600 ? 26 : 'clamp(26px, 3vw, 32px)',
            fontWeight: 700,
            letterSpacing: '-0.5px',
            textAlign: 'center',
            color: 'var(--text)',
            lineHeight: 1.25,
            marginBottom: 16,
            maxWidth: 720,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          Someone to call at 2 a.m.
          <br />
          <em style={{ color: 'var(--pink)', fontStyle: 'italic' }}>Without calling anyone.</em>
        </h2>

        <p
          style={{
            fontSize: 15,
            fontWeight: 400,
            color: '#7a6b63',
            textAlign: 'center',
            maxWidth: 560,
            margin: '0 auto 48px',
            lineHeight: 1.75,
            fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
          }}
        >
          Meet Nova. She&apos;s here to listen, to ground you, and to hold space for the questions you can&apos;t ask anyone else. The
          2 a.m. spiral, the symptom you&apos;ve been Googling for weeks, the worry you haven&apos;t said out loud yet.
        </p>

        <div
          style={{
            width: '100%',
            maxWidth: 720,
            margin: '0 auto',
          }}
        >
          <div
            style={{
              background: 'var(--card)',
              borderRadius: m600 ? 14 : 20,
              border: '1px solid rgba(212, 83, 126, 0.15)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: CHAT_CARD_SHADOW,
              minHeight: 500,
            }}
          >
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1.5px solid var(--pink)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexShrink: 0,
              }}
            >
              <NovaAvatarImg size={32} alt="" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                  Nova — your OrivonAI companion
                </p>
                <p style={{ fontSize: 11, color: 'var(--muted)', margin: 0 }}>Fertility journey · Available 24/7</p>
              </div>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--sage)',
                  flexShrink: 0,
                }}
                aria-hidden
              />
            </div>

            <div
              style={{
                padding: '16px 18px 18px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <NovaAvatarImg size={26} alt="" />
                <div
                  style={{
                    background: 'rgb(249, 246, 241)',
                    border: '1px solid rgba(237, 229, 216, 0.8)',
                    borderRadius: '0 12px 12px 12px',
                    padding: '10px 14px',
                    fontSize: m600 ? 13 : 13.5,
                    color: 'rgb(75, 21, 40)',
                    lineHeight: 1.55,
                    maxWidth: 'calc(100% - 80px)',
                    fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                  }}
                >
                  Hi Maya 💗 Your retrieval is tomorrow — I&apos;ve been thinking about you. How are you feeling? I have a
                  prep note ready and a thread from 18 women who did this last week.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', gap: 8 }}>
                <div
                  style={{
                    background: 'var(--pink)',
                    borderRadius: '12px 12px 0 12px',
                    padding: '10px 14px',
                    fontSize: m600 ? 13 : 13.5,
                    color: 'white',
                    lineHeight: 1.55,
                    maxWidth: m600 ? '90%' : '72%',
                    fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                  }}
                >
                  Honestly terrified. I keep reading about OHSS and now I&apos;m spiraling.
                </div>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: 'var(--pink)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 600,
                    color: 'white',
                    flexShrink: 0,
                  }}
                  aria-hidden
                >
                  M
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <NovaAvatarImg size={26} alt="" />
                <div
                  style={{
                    background: 'rgb(249, 246, 241)',
                    border: '1px solid rgba(237, 229, 216, 0.8)',
                    borderRadius: '0 12px 12px 12px',
                    padding: '10px 14px',
                    fontSize: m600 ? 13 : 13.5,
                    color: 'rgb(75, 21, 40)',
                    lineHeight: 1.55,
                    maxWidth: 'calc(100% - 80px)',
                    fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                  }}
                >
                  That fear makes complete sense. OHSS risk is real but manageable — severe OHSS affects fewer than ~2% of
                  cycles. You&apos;ve done the hard part. Tomorrow is just the next step.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <NovaAvatarImg size={26} alt="" />
                <div
                  style={{
                    background: 'rgb(249, 246, 241)',
                    border: '1px solid rgba(237, 229, 216, 0.8)',
                    borderRadius: '0 12px 12px 12px',
                    padding: '10px 14px',
                    fontSize: m600 ? 13 : 13.5,
                    color: 'rgb(75, 21, 40)',
                    lineHeight: 1.55,
                    maxWidth: 'calc(100% - 80px)',
                    fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                  }}
                >
                  The signs to watch for after retrieval: bloating that gets worse after 48 hours; difficulty
                  breathing; or less than 500ml urine in a day. If any of those — call your clinic right away.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <NovaAvatarImg size={26} alt="" />
                <div
                  style={{
                    background: 'rgb(225, 245, 238)',
                    border: '1px solid rgba(14, 110, 86, 0.15)',
                    borderRadius: '0 12px 12px 12px',
                    padding: '12px 14px',
                    maxWidth: 'calc(100% - 80px)',
                    fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'rgb(14,110,86)',
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    Resources in your chapter
                  </div>
                  <div style={{ fontSize: 13, color: 'rgb(75,21,40)', lineHeight: 1.5 }}>
                    I&apos;ve pulled up the full OHSS guide in your Fertility chapter — clinically reviewed, written
                    for where you are right now. Want me to open it?
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'rgb(14,110,86)',
                      marginTop: 8,
                      display: 'block',
                      cursor: 'pointer',
                    }}
                  >
                    Open OHSS guide in OrivonAI →
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                background: 'rgb(249, 246, 241)',
                borderTop: `1px solid ${BORDER_MUTED}`,
                padding: '14px 16px',
                display: 'flex',
                flexDirection: m ? 'column' : 'row',
                alignItems: m ? 'stretch' : 'center',
                gap: 10,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 44,
                  display: 'flex',
                  alignItems: 'center',
                  background: '#fffdf9',
                  border: `1px solid ${BORDER_MUTED}`,
                  borderRadius: 999,
                  padding: '10px 16px',
                  fontSize: 14,
                  fontWeight: 400,
                  color: '#7a6b63',
                  fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                  boxSizing: 'border-box',
                }}
              >
                Ask Nova anything about your journey…
              </div>
              <button
                type="button"
                onClick={() => openNova("Honestly terrified. I keep reading about OHSS and now I'm spiraling.")}
                style={{
                  background: 'var(--pink)',
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '7px 14px',
                  borderRadius: 20,
                  whiteSpace: 'nowrap',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  flexShrink: 0,
                }}
              >
                Open in Nova
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
